import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Building2,
  ShieldCheck,
  Upload,
  FileText,
  Trash2,
  Plus,
  Download,
  CheckCircle2,
  AlertCircle,
  History,
  Pause,
  Play,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KYC_CATEGORIES = [
  {
    key: 'gst_certificate',
    label: 'GST Certificate',
    accent: 'from-emerald-500 to-emerald-700',
    description: 'GST registration certificate (REG-06)',
    required: true,
  },
  {
    key: 'pan_card',
    label: 'PAN Card',
    accent: 'from-sky-500 to-sky-700',
    description: 'Vendor / proprietor PAN card',
    required: true,
  },
  {
    key: 'msme_certificate',
    label: 'MSME / Udyam Certificate',
    accent: 'from-violet-500 to-violet-700',
    description: 'Udyam registration (if applicable)',
    required: true,
  },
  {
    key: 'cancelled_cheque',
    label: 'Cancelled Cheque / Bank Letter',
    accent: 'from-amber-500 to-amber-700',
    description: 'For bank account verification',
    required: true,
  },
];

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const VendorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = !!(user?.is_super_admin || user?.role === 'admin');

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [customFile, setCustomFile] = useState(null);
  const [uploadingCustom, setUploadingCustom] = useState(false);

  const fileInputRefs = useRef({}); // category -> input

  const fetchVendor = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vendors/${id}`, { withCredentials: true });
      setVendor(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load vendor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleCategoryUpload = async (categoryKey, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10MB limit');
      return;
    }
    setUploadingCategory(categoryKey);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/vendors/${id}/kyc?category=${encodeURIComponent(categoryKey)}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(res.data?.message || 'Document uploaded');
      // If the AI extracted authoritative fields (e.g. bank details from a
      // cancelled cheque), surface the update so the user knows their vendor
      // record was just improved automatically.
      const autoFields = res.data?.auto_updates || {};
      const autoKeys = Object.keys(autoFields);
      if (autoKeys.length > 0) {
        const labels = {
          gstin: 'GSTIN',
          pan: 'PAN',
          bank_account_number: 'Bank A/C',
          ifsc_code: 'IFSC',
          address: 'Address',
        };
        const pretty = autoKeys.map((k) => labels[k] || k).join(', ');
        toast.success(`AI auto-filled vendor: ${pretty}`, { duration: 5000 });
      }
      await fetchVendor();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingCategory(null);
    }
  };

  const handleCustomUpload = async () => {
    if (!customLabel.trim()) {
      toast.error('Please enter a label for this document');
      return;
    }
    if (!customFile) {
      toast.error('Please choose a file');
      return;
    }
    setUploadingCustom(true);
    try {
      const fd = new FormData();
      fd.append('file', customFile);
      const params = new URLSearchParams({ category: 'other', label: customLabel.trim() });
      if (customNotes.trim()) params.append('notes', customNotes.trim());
      await axios.post(`${API}/vendors/${id}/kyc?${params.toString()}`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setCustomDialogOpen(false);
      setCustomLabel('');
      setCustomNotes('');
      setCustomFile(null);
      await fetchVendor();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingCustom(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/vendors/${id}/kyc/${docId}`, { withCredentials: true });
      toast.success('Document deleted');
      await fetchVendor();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDownload = (docId) => {
    // Open in a new tab — backend streams the original file
    window.open(`${API}/vendors/${id}/kyc/${docId}/download`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading vendor…</p>
      </div>
    );
  }
  if (!vendor) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/vendors')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Vendor not found.
          </CardContent>
        </Card>
      </div>
    );
  }

  const docs = vendor.kyc_documents || [];
  const docsByCategory = {};
  docs.forEach((d) => {
    if (!docsByCategory[d.category]) docsByCategory[d.category] = [];
    docsByCategory[d.category].push(d);
  });
  const customDocs = docsByCategory['other'] || [];

  const completedRequired = KYC_CATEGORIES.filter(
    (c) => c.required && (docsByCategory[c.key]?.length || 0) > 0
  ).length;
  const totalRequired = KYC_CATEGORIES.filter((c) => c.required).length;
  const kycComplete = completedRequired === totalRequired;

  return (
    <div className="space-y-6 min-w-0" data-testid="vendor-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/vendors')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Vendors
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight truncate">
              {vendor.name}
            </h1>
            <p className="text-sm text-muted-foreground">Vendor onboarding & KYC</p>
          </div>
        </div>
        <div>
          <Badge
            className={`gap-1.5 ${
              kycComplete
                ? 'bg-emerald-600 hover:bg-emerald-600 text-white'
                : 'bg-amber-500 hover:bg-amber-500 text-white'
            }`}
            data-testid="kyc-status-badge"
          >
            {kycComplete ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5" />
            )}
            KYC {completedRequired}/{totalRequired}
          </Badge>
        </div>
      </div>

      {/* Vendor profile snapshot */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Vendor Profile
          </CardTitle>
          <CardDescription>Basic details captured during onboarding</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <Field label="GSTIN" value={vendor.gstin} mono />
            <Field label="PAN" value={vendor.pan} mono />
            <Field label="Email" value={vendor.email} />
            <Field label="Phone" value={vendor.phone} />
            <Field label="Bank A/c" value={vendor.bank_account_number} mono />
            <Field label="IFSC" value={vendor.ifsc_code} mono />
            <Field label="Address" value={vendor.address} colSpan />
            <Field label="Remarks" value={vendor.remarks} colSpan />
          </div>
        </CardContent>
      </Card>

      {/* Hold history — append-only timeline of every hold/release toggle */}
      <Card className="border-border" data-testid="vendor-hold-history-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            Hold History
            {vendor.on_hold && (
              <Badge
                variant="outline"
                className="ml-2 bg-rose-100 border-rose-300 text-rose-800 gap-1"
                data-testid="vendor-detail-hold-badge"
              >
                <Pause className="h-3 w-3" />
                Currently on hold
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Audit log of every time this vendor was placed on hold or released.
            Hold blocks the vendor's invoices from being added to new approvals.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(!vendor.hold_history || vendor.hold_history.length === 0) ? (
            <p className="text-sm text-muted-foreground italic">
              No hold events yet. This vendor has never been put on hold.
            </p>
          ) : (
            <ol className="space-y-3" data-testid="vendor-hold-history-list">
              {[...vendor.hold_history].reverse().map((h, idx) => {
                const isHold = h.action === 'hold';
                return (
                  <li
                    key={`${h.at}-${idx}`}
                    className="flex gap-3 items-start text-sm border-l-2 pl-4 py-1.5"
                    style={{
                      borderLeftColor: isHold ? '#fb923c' /* amber-400 */ : '#34d399' /* emerald-400 */,
                    }}
                    data-testid={`vendor-hold-history-row-${idx}`}
                  >
                    <div className="mt-0.5">
                      {isHold ? (
                        <Pause className="h-4 w-4 text-amber-600" />
                      ) : (
                        <Play className="h-4 w-4 text-emerald-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant="outline"
                          className={
                            isHold
                              ? 'bg-amber-100 border-amber-300 text-amber-900 text-[10px]'
                              : 'bg-emerald-100 border-emerald-300 text-emerald-900 text-[10px]'
                          }
                        >
                          {isHold ? 'Placed on hold' : 'Released from hold'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {h.at ? new Date(h.at).toLocaleString('en-IN', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }) : '—'}
                        </span>
                        {h.by && (
                          <span className="text-xs text-muted-foreground">
                            · by <span className="font-medium text-foreground">{h.by}</span>
                          </span>
                        )}
                      </div>
                      {h.reason && (
                        <p className="text-sm text-muted-foreground mt-1 italic">
                          "{h.reason}"
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* Required KYC documents */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h2 className="text-lg sm:text-xl font-heading font-semibold tracking-tight flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              Required KYC Documents
            </h2>
            <p className="text-xs text-muted-foreground">
              Upload one document per category. Accepted formats: PDF, JPG, PNG (max 10MB each).
            </p>
          </div>
          {canManage && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCustomDialogOpen(true)}
              data-testid="add-custom-doc-button"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add custom document
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {KYC_CATEGORIES.map((cat) => {
            const list = docsByCategory[cat.key] || [];
            const has = list.length > 0;
            return (
              <Card
                key={cat.key}
                data-testid={`kyc-card-${cat.key}`}
                className="border-border overflow-hidden flex flex-col"
              >
                <div className={`h-1 w-full bg-gradient-to-r ${cat.accent}`} />
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-sm font-heading">{cat.label}</CardTitle>
                    {has ? (
                      <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px] px-1.5 py-0 h-5 gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Uploaded
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
                        Pending
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="text-xs">{cat.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-2 flex-1 flex flex-col">
                  <div className="space-y-2 flex-1">
                    {list.map((d) => (
                      <DocRow
                        key={d.id}
                        doc={d}
                        canManage={canManage}
                        onDownload={() => handleDownload(d.id)}
                        onDelete={() => handleDelete(d.id)}
                      />
                    ))}
                    {list.length === 0 && (
                      <div className="text-xs text-muted-foreground italic">
                        No document uploaded yet.
                      </div>
                    )}
                  </div>

                  {canManage && (
                    <>
                      <input
                        ref={(el) => (fileInputRefs.current[cat.key] = el)}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                        className="hidden"
                        data-testid={`kyc-file-input-${cat.key}`}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          handleCategoryUpload(cat.key, f);
                          e.target.value = '';
                        }}
                      />
                      <Button
                        size="sm"
                        variant={has ? 'outline' : 'default'}
                        className="mt-3 w-full"
                        disabled={uploadingCategory === cat.key}
                        onClick={() => fileInputRefs.current[cat.key]?.click()}
                        data-testid={`kyc-upload-button-${cat.key}`}
                      >
                        <Upload className="h-4 w-4 mr-1.5" />
                        {uploadingCategory === cat.key
                          ? 'Uploading…'
                          : has
                          ? 'Upload another'
                          : 'Upload'}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Custom / additional documents */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Additional Documents
          </CardTitle>
          <CardDescription>
            Any extra documents you collect for this vendor — agreements, NDAs, registrations, etc.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {customDocs.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No additional documents yet.
              {canManage && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={() => setCustomDialogOpen(true)}
                    className="text-primary hover:underline font-medium"
                  >
                    Add one
                  </button>
                  .
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {customDocs.map((d) => (
                <DocRow
                  key={d.id}
                  doc={d}
                  showLabel
                  canManage={canManage}
                  onDownload={() => handleDownload(d.id)}
                  onDelete={() => handleDelete(d.id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Custom upload dialog */}
      <Dialog open={customDialogOpen} onOpenChange={setCustomDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add a custom document</DialogTitle>
            <DialogDescription>
              For agreements, registrations, or anything outside the standard KYC set.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="custom-label">Label *</Label>
              <Input
                id="custom-label"
                placeholder="e.g. NDA, Service Agreement, Trade Licence"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                data-testid="custom-label-input"
              />
            </div>
            <div>
              <Label htmlFor="custom-notes">Notes (optional)</Label>
              <Input
                id="custom-notes"
                placeholder="Any extra context"
                value={customNotes}
                onChange={(e) => setCustomNotes(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="custom-file">File *</Label>
              <Input
                id="custom-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                onChange={(e) => setCustomFile(e.target.files?.[0] || null)}
                data-testid="custom-file-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCustomUpload}
              disabled={uploadingCustom}
              data-testid="custom-upload-submit"
            >
              {uploadingCustom ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, value, mono, colSpan }) => (
  <div className={colSpan ? 'sm:col-span-2 lg:col-span-3' : ''}>
    <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
      {label}
    </div>
    <div className={`mt-0.5 ${mono ? 'font-mono text-sm' : 'text-sm'} ${value ? '' : 'text-muted-foreground italic'}`}>
      {value || '—'}
    </div>
  </div>
);

const DocRow = ({ doc, showLabel = false, canManage, onDownload, onDelete }) => {
  const display = showLabel
    ? doc.label || doc.filename
    : doc.filename;
  const sub = showLabel && doc.label && doc.filename !== doc.label ? doc.filename : '';

  return (
    <div
      className="flex items-center gap-2 rounded-md border border-border bg-muted/20 px-2.5 py-2 text-xs"
      data-testid={`kyc-doc-${doc.id}`}
    >
      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{display}</div>
        <div className="text-muted-foreground truncate">
          {sub ? `${sub} · ` : ''}
          {fmtBytes(doc.size_bytes)}
          {doc.uploaded_at
            ? ` · ${new Date(doc.uploaded_at).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}`
            : ''}
        </div>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0"
        title="Download"
        onClick={onDownload}
      >
        <Download className="h-3.5 w-3.5" />
      </Button>
      {canManage && (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          title="Delete"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      )}
    </div>
  );
};

export default VendorDetail;
